/**
 * シフト表の表示(2026-09-15 MTG。2026-09-17 実装)。
 *  - 提出と確定の差: シフト表のセル(管理者が直した後)= 確定、本人が出した時間 = 提出(P-45 → D-130 で決定)。
 *    直した日の提出時間は、その日の最初の変更の「変更前」から戻す。
 *    提出した後は本人が出し直せない(変更申請から)ので、変更履歴は管理者の代理入力でも消さずに足していく
 *  - 原価率の条件付き書式: 「◯%以上は青」のような条件を管理者が入れる(settings/shift_display)。上の条件から順に見て最初に当てはまった色
 */
import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import { getDb } from '@core/lib/firebase';
import { dayHours, defaultBreakMin, roundQuarter, type ShiftChangeLog, type ShiftSubmission, type SubmitDay } from './shiftSubmitRepo';

/** シフト表のセルの文字(「9-18」「欠勤」「当欠」。休みは空) */
export function cellText(d: Pick<SubmitDay, 'start' | 'end' | 'status'> | undefined): string {
  return !d ? '' : d.status === 'absent' ? '欠勤' : d.status === 'same_day' ? '当欠' : `${d.start}-${d.end}`;
}

/** 変更履歴に残す文字。休憩が既定と違う日・中抜けのある日は括弧で足す(代理入力で休憩だけ直したときも分かるように) */
export function logTextOf(d: SubmitDay | undefined): string {
  const base = cellText(d) || '休み';
  if (!d || d.status) return base;
  const extra = [
    d.break_min !== undefined && d.break_min !== defaultBreakMin(d) ? `休憩${d.break_min}分` : '',
    d.gap_hours ? `中抜け${d.gap_hours}h` : '',
  ]
    .filter(Boolean)
    .join('・');
  return extra ? `${base}(${extra})` : base;
}

/**
 * シフト表のセルの文字(「9-18」「8.5-13.5」「欠勤」「当欠」「休み」)→ 時間。欠勤・休みは0。
 * 文字には休憩が入っていないので、**休憩は既定値(6時間以上で1時間)で引く**(D-127。dayHours と同じ数え方にそろえる)
 */
export function hoursOfCellText(text: string): number {
  const m = text.trim().match(/^(\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)$/);
  if (!m) return 0;
  return dayHours({ start: Number(m[1]), end: Number(m[2]) });
}

/** 変更の記録の「変更前」の時間。2026-09-22 以降の記録は before_hours、それより前は文字から */
export function beforeHoursOf(log: Pick<ShiftChangeLog, 'before' | 'before_hours'>): number {
  return log.before_hours ?? hoursOfCellText(log.before);
}

/**
 * 日 → その日の**最初の**変更の記録(=「変更前」に本人が提出したときの姿が入っている)。
 * logs は新しい順。後ろほど古いので、上書きしていくと最初の変更が残る
 */
export function firstChangeByDay(logs: ShiftChangeLog[] | undefined): Map<string, ShiftChangeLog> {
  const out = new Map<string, ShiftChangeLog>();
  for (const l of logs ?? []) out.set(l.day, l);
  return out;
}

/** 提出した時間の合計(h)。管理者が直した日は最初の変更の「変更前」を使う */
export function submittedHoursOf(s: Pick<ShiftSubmission, 'days' | 'logs'>): number {
  const firstBefore = firstChangeByDay(s.logs);
  let h = 0;
  for (const d of new Set([...Object.keys(s.days), ...firstBefore.keys()])) {
    h += firstBefore.has(d) ? beforeHoursOf(firstBefore.get(d)!) : dayHours(s.days[d]);
  }
  return roundQuarter(h);
}

/** 確定 − 提出(h)。+ は提出より多く入った */
export function shiftDiffHours(s: Pick<ShiftSubmission, 'days' | 'logs'>): number {
  const confirmed = Object.values(s.days).reduce((a, d) => a + dayHours(d), 0);
  return roundQuarter(confirmed - submittedHoursOf(s));
}

// ---- 提出からの変更の種類(2026-09-24 発注者指示。色分けに使う) -------------------------

/**
 * 提出したシフトからどう変わったか。
 *  - `add` 稼働追加: 提出は休み(0h)だった日に稼働が入った
 *  - `extend` 稼働時間延長 / `shorten` 稼働時間短縮: 提出より時間が増えた・減った(休憩・中抜けを引いた後で比べる)
 *  - `off` 休みに変更: 稼働していた日を休みにした
 *  - `other`: 欠勤・当欠の記録(赤のまま)、時間帯だけ動いて合計が同じとき。色は変えず、これまでどおり変更ありの印だけ
 */
export type ShiftChangeKind = 'add' | 'extend' | 'shorten' | 'off' | 'other';

/** 色を分ける4種類(凡例の並び) */
export const SHIFT_CHANGE_KINDS = ['add', 'extend', 'shorten', 'off'] as const;
export const SHIFT_CHANGE_LABELS: Record<ShiftChangeKind, string> = {
  add: '稼働追加',
  extend: '稼働時間延長',
  shorten: '稼働時間短縮',
  off: '休みに変更',
  other: '変更あり',
};
/** セルの右上と凡例に出す印(色だけで分からないように。design-system.md §3) */
export const SHIFT_CHANGE_MARKS: Record<ShiftChangeKind, string> = { add: '+', extend: '▲', shorten: '▼', off: '×', other: '' };

/** 提出(最初の変更の「変更前」)と確定(いまのセル)を比べた変更の種類 */
export function changeKindOf(log: Pick<ShiftChangeLog, 'before' | 'before_hours'>, now: SubmitDay | undefined): ShiftChangeKind {
  const before = beforeHoursOf(log);
  if (!now) return 'off';
  if (now.status) return 'other'; // 欠勤・当欠は本人の連絡を受けて付ける記録(これまでどおり赤)
  const after = dayHours(now);
  if (before <= 0) return 'add'; // 提出は休み(または欠勤)だった
  if (after > before) return 'extend';
  if (after < before) return 'shorten';
  return 'other'; // 時間帯だけ動いて合計は同じ
}

/** +2.5 / −3 / 空(差なし) */
export function fmtDiff(h: number): string {
  if (h === 0) return '';
  // 15分単位(0.25)まであるので、小数は2桁まで出す(0.25 → 0.25、2.5 → 2.5)
  const abs = String(Math.round(Math.abs(h) * 100) / 100);
  return h > 0 ? `+${abs}` : `−${abs}`;
}

// ---- 原価率の条件付き書式 ----------------------------------------------------------

export type RuleOp = 'gte' | 'lt';
export type RuleColor = 'blue' | 'red' | 'green' | 'amber';
export type CostRateRule = { op: RuleOp; value: number; color: RuleColor };

export const RULE_OP_LABELS: Record<RuleOp, string> = { gte: '以上', lt: '未満' };
export const RULE_COLOR_LABELS: Record<RuleColor, string> = { blue: '青', red: '赤', green: '緑', amber: '黄' };
export const RULE_COLOR_STYLES: Record<RuleColor, { color: string; background: string }> = {
  blue: { color: 'var(--blue)', background: 'var(--blue-bg)' },
  red: { color: 'var(--red)', background: 'var(--red-bg)' },
  green: { color: 'var(--green)', background: 'var(--green-bg)' },
  amber: { color: 'var(--amber)', background: 'var(--amber-bg)' },
};

/** 上から順に見て、最初に当てはまった条件の色。原価率が無い(—)ときは色を付けない */
export function colorOfCostRate(rate: number | null, rules: CostRateRule[]): RuleColor | null {
  if (rate === null || !Number.isFinite(rate)) return null;
  for (const r of rules) {
    if (r.op === 'gte' ? rate >= r.value : rate < r.value) return r.color;
  }
  return null;
}

const PATH = ['settings', 'shift_display'] as const;

export async function fetchCostRateRules(): Promise<CostRateRule[]> {
  const data = (await getDoc(doc(getDb(), ...PATH))).data() ?? {};
  const list = Array.isArray(data.cost_rate_rules) ? (data.cost_rate_rules as CostRateRule[]) : [];
  return list.filter((r) => (r.op === 'gte' || r.op === 'lt') && Number.isFinite(r.value) && r.color in RULE_COLOR_STYLES);
}

export async function saveCostRateRules(rules: CostRateRule[], userId: string): Promise<void> {
  for (const r of rules) if (!Number.isFinite(r.value) || r.value < 0) throw new Error('原価率の値は0以上の数字で入れてください');
  await setDoc(doc(getDb(), ...PATH), { cost_rate_rules: rules, updated_by: userId, updated_at: serverTimestamp() }, { merge: true });
}
